import type { ApiResponse } from './estClient';

import { estClient } from './estClient';

/** EasyBusy's `ChatProviderSource`, mirrors `EasyBusyClient::PROVIDERS` */
const PROVIDER = {
  TELEGRAM: 'Telegram',
  TELEGRAM_BOT: 'TelegramBot',
  VIBER_BOT: 'ViberBot',
  VIBER_BUSINESS: 'ViberBusiness',
  VIBER_PERSONAL: 'ViberPersonal',
  ECHAT_VIBER: 'EchatViber',
  WHATSAPP: 'WhatsApp',
  WHATSAPP_PERSONAL: 'WhatsAppPersonal',
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  WEB_CHAT: 'WebChat',
  TIKTOK: 'TikTok',
} as const;

/** Who wrote the last message of a chat summary, mirrors `EasyBusyChatSummaryDto::FROM_*` */
const MESSAGE_FROM = {
  CLIENT: 'client',
  MANAGER: 'manager',
} as const;

/** What a manager should see for each provider - several of them share one product name */
const PROVIDER_LABEL: Record<EasyBusyProvider, string> = {
  [PROVIDER.TELEGRAM]: 'Telegram',
  [PROVIDER.TELEGRAM_BOT]: 'Telegram Bot',
  [PROVIDER.VIBER_BOT]: 'Viber Bot',
  [PROVIDER.VIBER_BUSINESS]: 'Viber Business',
  [PROVIDER.VIBER_PERSONAL]: 'Viber',
  [PROVIDER.ECHAT_VIBER]: 'Viber',
  [PROVIDER.WHATSAPP]: 'WhatsApp',
  [PROVIDER.WHATSAPP_PERSONAL]: 'WhatsApp',
  [PROVIDER.INSTAGRAM]: 'Instagram',
  [PROVIDER.FACEBOOK]: 'Messenger',
  [PROVIDER.WEB_CHAT]: 'Web Chat',
  [PROVIDER.TIKTOK]: 'TikTok',
};

class EasyBusyApi {
  get PROVIDER() {
    return PROVIDER;
  }

  get MESSAGE_FROM() {
    return MESSAGE_FROM;
  }

  /** Falls back to the raw enum value for a provider EasyBusy added and we haven't mapped yet */
  providerLabel(provider: string): string {
    return PROVIDER_LABEL[provider as EasyBusyProvider] ?? provider;
  }

  async getStatus(): Promise<EasyBusyStatus> {
    return estClient.get<ApiResponse<EasyBusyStatus>>('/api/easybusy/status');
  }

  async connect(): Promise<{ enabled: boolean }> {
    return estClient.post<ApiResponse<{ enabled: boolean }>>(
      '/api/easybusy/connect',
    );
  }

  async auth(): Promise<EasyBusyAuth> {
    return estClient.post<ApiResponse<EasyBusyAuth>>('/api/easybusy/auth');
  }

  async getProvidersStatus(): Promise<EasyBusyProvidersStatus> {
    return estClient.get<ApiResponse<EasyBusyProvidersStatus>>(
      '/api/easybusy/providers/status',
    );
  }

  async syncProviders(): Promise<EasyBusyProvidersStatus> {
    return estClient.post<ApiResponse<EasyBusyProvidersStatus>>(
      '/api/easybusy/providers/sync',
    );
  }

  async getWebhookSyncStatus(): Promise<EasyBusyWebhookSyncStatus> {
    return estClient.get<ApiResponse<EasyBusyWebhookSyncStatus>>(
      '/api/easybusy/webhook/status',
    );
  }

  async syncWebhooks(): Promise<EasyBusyWebhookSyncStatus> {
    return estClient.post<ApiResponse<EasyBusyWebhookSyncStatus>>(
      '/api/easybusy/webhook/sync',
    );
  }

  async listUserSettings(): Promise<EasyBusyUserSetting[]> {
    const resp = await estClient.get<
      ApiResponse<{ users: EasyBusyUserSetting[] }>
    >('/api/easybusy/users');

    return resp.users;
  }

  async setUsersEnabled({
    userIds,
    isEnabled,
  }: {
    userIds: string[];
    isEnabled: boolean;
  }): Promise<EasyBusyUserSetting[]> {
    const resp = await estClient.post<
      ApiResponse<{ users: EasyBusyUserSetting[] }>
    >('/api/easybusy/users/toggle', { body: { userIds, isEnabled } });

    return resp.users;
  }

  async getUsersConnectStatus(): Promise<EasyBusyUsersConnectStatus> {
    return estClient.get<ApiResponse<EasyBusyUsersConnectStatus>>(
      '/api/easybusy/users/connect/status',
    );
  }

  async connectUsers(): Promise<EasyBusyUsersConnectResult> {
    return estClient.post<ApiResponse<EasyBusyUsersConnectResult>>(
      '/api/easybusy/users/connect',
    );
  }

  async findChatLead({
    accountId,
    chatId,
  }: {
    accountId: string;
    chatId: string;
  }): Promise<EasyBusyChatLead> {
    return estClient.get<ApiResponse<EasyBusyChatLead>>(
      '/api/easybusy/chat/lead',
      { query: { account_id: accountId, chat_id: chatId } },
    );
  }

  /** Overrides what the messages decided, until the next message */
  async setChatNeedsReply({
    accountId,
    chatId,
    needsReply,
  }: {
    accountId: string;
    chatId: string;
    needsReply: boolean;
  }): Promise<EasyBusyLeadChat> {
    const resp = await estClient.post<ApiResponse<{ chat: EasyBusyLeadChat }>>(
      '/api/easybusy/chat/needs-reply',
      {
        body: {
          account_id: accountId,
          chat_id: chatId,
          needs_reply: needsReply,
        },
      },
    );

    return resp.chat;
  }

  async createLeadChat({
    leadId,
    provider,
    accountId,
    phone,
  }: {
    leadId: string;
    provider: string;
    accountId: string;
    phone: string;
  }): Promise<EasyBusyCreatedChat> {
    return estClient.post<ApiResponse<EasyBusyCreatedChat>>(
      `/api/easybusy/lead/${leadId}/chat`,
      { body: { provider, account_id: accountId, phone } },
    );
  }
}

export const easyBusyApi = new EasyBusyApi();

export interface EasyBusyAuth {
  companyId: string | null;
  accessToken: string | null;
  email: string | null;
}

export interface EasyBusyStatus {
  enabled: boolean;
  userEnabled: boolean;
  pluginUrl: string;
  consoleUrl: string;
  currentUserEmail: string | null;
}

export interface EasyBusyProvidersStatus {
  synced: boolean;
  providersTotal: number;
  accountsTotal: number;
  syncedAt: string | null;
}

export interface EasyBusyWebhookSyncStatus {
  registered: boolean;
  upToDate: boolean;
  enabled: boolean | null;
  lastReceivedAt: string | null;
}

export interface EasyBusyUserSetting {
  userId: string;
  username: string;
  email: string;
  isEnabled: boolean;
}

export interface EasyBusyUsersConnectStatus {
  matchedCount: number;
  totalUsers: number;
  connectedAt: string | null;
}

export interface EasyBusyUsersConnectResult extends EasyBusyUsersConnectStatus {
  provisionErrors: string[];
}

export interface EasyBusyLeadChat {
  provider: string;
  account_id: string;
  account_name: string | null;
  account_identifier: string | null;
  chat_id: string;
  chat_updated_at: string | null;
  /** Null until the contact has written */
  last_client_message_at: string | null;
  /** Null until a manager has replied */
  last_manager_message_at: string | null;
  /** Who wrote last; null - nobody has written yet */
  last_message_from: EasyBusyMessageFrom | null;
  has_unread: boolean;
  /**
   * The conversation is not finished on our side: set by the contact's message, cleared by a manager's reply,
   * or switched by hand until the next message
   */
  needs_reply: boolean;
}

/** Several chats as one: of a lead in the lead list, of a contact in the lead popup */
export interface EasyBusyChatSummary {
  /** At least one of the chats needs a reply */
  needs_reply: boolean;
  /** At least one of the chats has messages nobody has read or answered yet */
  has_unread: boolean;
  /** The message fields come from one chat - the latest needing a reply, otherwise the latest */
  last_message_from: EasyBusyMessageFrom | null;
  last_messages: {
    /** When the contact last wrote in that chat */
    client_at: string | null;
    /** When a manager last replied in that chat */
    manager_at: string | null;
  };
}

/** A provider a new chat can be started on - the number has no chat there yet */
export interface EasyBusyNewChatOption {
  provider: string;
  account_id: string;
  account_name: string | null;
  account_identifier: string | null;
}

export interface EasyBusyCreatedChat {
  chat_id: string;
  /** The other lead that owns this chat, null when it is ours */
  linked_lead_id: number | null;
}

export interface EasyBusyLeadPhoneChats {
  /** Exactly as the lead record spells it, so it can be matched without normalizing */
  phone: string;
  /** The same number as the API expects it back on `createLeadChat` */
  normalized_phone: string;
  /** Every chat of this number */
  chats: EasyBusyLeadChat[];
  new_chat_options: EasyBusyNewChatOption[];
}

/** The lead a chat belongs to, and the chat itself - both null when it belongs to none */
export interface EasyBusyChatLead {
  lead_id: number | null;
  chat: EasyBusyLeadChat | null;
}

/** Everything the lead popup shows of the lead's messengers */
export interface EasyBusyLeadChats {
  /** Chats per phone */
  phones: EasyBusyLeadPhoneChats[];
  /** Chats with no phone attached - a web chat, Instagram, a number since removed */
  other_chats: EasyBusyLeadChat[];
  /** Such chats of a lead merged in as an additional contact, keyed by that contact's position */
  additional_other_chats: Record<string, EasyBusyLeadChat[]>;
  /** At least one chat on this lead has messages nobody has read or answered yet */
  has_unread: boolean;
  /** All chats of each contact as one, informational; a contact without chats is absent */
  contacts_summary: {
    main: EasyBusyChatSummary | null;
    /** By additional contact position */
    additional: Record<string, EasyBusyChatSummary>;
  };
}

export type EasyBusyProvider = (typeof PROVIDER)[keyof typeof PROVIDER];

export type EasyBusyMessageFrom =
  (typeof MESSAGE_FROM)[keyof typeof MESSAGE_FROM];
